import { ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { Form, FormAdapter, FormAlert, FormContainerOptions, FormControlType, FormEvent, FormValues } from '@omegagrid/form';
import rp from '@reporting/rp-app-host';
import { HelpdeskLists, REQUEST_PRIORITIES, REQUEST_TYPES } from "../types";
import { Accounting, AccountingRights } from '@reporting/rp-types/core';
import { Ticket, Message } from '@reporting/rp-types/helpdesk';
import { FollowerTreeAdapter, joinFollowers } from './followerTreeAdapter';

export class NewTicketFormAdapter extends FormAdapter {

	public id: number;

	private formFields = ['subject', 'priority', 'id_type', 'accounting', 'who_follower', 'escalation'] as const;
	private _lists: HelpdeskLists = {
		id_type: REQUEST_TYPES.map(item => ({ k: item.k, v: msg(item.v) })),
		priority: REQUEST_PRIORITIES.map(item => ({ k: item.k, v: msg(item.v) })),
		accounting: [],
	};

	getColumns() {
		return [
			{ width: 100 },
			{ width: null },
		];
	}

	/** The company picked in the form - the current one until the user changes it. */
	private accounting(): number {
		return (this.component?.values?.accounting as number) ?? rp.accountingProvider.a;
	}

	/** The follower belongs to the company of the request: a change of the company drops the one picked for the previous. */
	protected attached(form: Form) {
		form.addEventListener('form.change', (e: FormEvent) => {
			if (e.key === 'accounting' && form.values.who_follower != null) form.setValue('who_follower', null);
		});
	}

	async getControls() {
		// the companies the user may create a request for (the right helpdesk; every company for an SA)
		const accountings = await rp.ms('accounting').get<Accounting[]>('/accountings', { right: AccountingRights.Helpdesk });
		this._lists.accounting = (accountings ?? []).map(a => ({ k: a.accounting, v: `${a.accounting} ${a.company}` }));
		// the current company by default - the responsible person is preset from its MAIL_ETL (rp-ms-helpdesk TicketService)
		const accounting = this._lists.accounting.find(a => a.k === rp.accountingProvider.a) ?? this._lists.accounting[0];
		return [
			{ row: 0, col: 0, type: FormControlType.Label, value: msg('Subject'), for: 'subject' },
			{ row: 0, col: 1, type: FormControlType.TextInput, key: 'subject', required: true, pattern: /^.{5,100}$/ },
			{ row: 1, col: 0, type: FormControlType.Label, value: msg('Priority'), for: 'priority' },
			{ row: 1, col: 1, type: FormControlType.List, key: 'priority', value: 1, required: true, content: {
				sourceData: this._lists.priority, options: { selectMode: 'single', autoClose: true }
			}},
			{ row: 2, col: 0, type: FormControlType.Label, value: msg('Type'), for: 'id_type' },
			{ row: 2, col: 1, type: FormControlType.List, key: 'id_type', value: 1, required: true, content: {
				sourceData: this._lists.id_type, options: { selectMode: 'single', autoClose: true }
			}},
			{ row: 3, col: 0, type: FormControlType.Label, value: msg('Company'), for: 'accounting' },
			{ row: 3, col: 1, type: FormControlType.List, key: 'accounting', value: accounting?.k, required: true, content: {
				sourceData: this._lists.accounting,	options: { selectMode: 'single', autoClose: true, search: true }
			}},
			{ row: 4, col: 0, type: FormControlType.Label, value: msg('Follower'), for: 'who_follower' },
			{ row: 4, col: 1, type: FormControlType.List, key: 'who_follower', adapter: new FollowerTreeAdapter(() => this.accounting()) },
			{ row: 5, col: 0, type: FormControlType.Label, value: msg('Escalation'), for: 'escalation' },
			{ row: 5, col: 1, type: FormControlType.Checkbox, key: 'escalation', value: false },
			{ row: 6, col: 0, cs: 2, type: FormControlType.Label, value: msg('You can specify your request in the next conversation.') },
			{ row: 7, col: 0, cs: 2, type: FormControlType.Label, value: msg('By creating the request, you will start the conversation.') },
		];
	}

	getOptions(): FormContainerOptions {
		return {
			caption: null,
			buttonsPosition: 'bottom',
			alertsPosition: 'bottom',
			buttons: [{ key: "save", text: msg("Create"), color: ColorTypes.Accent }, "close"]
		};
	}

	async save(values: Map<string|number, FormValues>): Promise<boolean|FormAlert[]> {
		const formValues = values.get(0);
		const ticket: Ticket = {
			...Object.fromEntries(this.formFields.map(key => [key, formValues[key]])),
			// the followers picked (the tuples of the multi-select list) as the server stores them: user names separated by semicolons
			who_follower: joinFollowers(formValues.who_follower),
		};
		const newTicket: Ticket = await rp.ms('helpdesk').post('tickets', ticket, { accounting: ticket.accounting });
		const sysMessage: Message = {
			id_request: newTicket.id_request,
			text: `${msg('Creation of request')} #${newTicket.id_request}.`,
			sys_message: true
		}
		await rp.ms('helpdesk').post(`messages`, sysMessage, { accounting: ticket.accounting });
		rp.host.open('helpdesk-ticket-' + newTicket.id_request, { title: newTicket.subject });
		return true;
	}

};
